import { useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { useMutation } from "@tanstack/react-query";
import { X } from "lucide-react";
import { apiClient } from "@/lib/api/client";
import { translateApiError } from "@/lib/errors/error-map";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordField } from "@/components/password-field";
import { isPasswordValid } from "@/lib/validation/password";

interface ChangePasswordDialogProps {
  /** Required so the PATCH request can authenticate even before session is fully wired. */
  accessToken: string;
  /** Called after a successful password change. Parent should clear session and prompt re-login. */
  onSuccess: () => void;
  onClose: () => void;
}

interface ChangePasswordBody {
  current_password: string;
  new_password: string;
}

/**
 * Modal forced when the backend returns a user with mustChangePassword=true.
 * Calls PATCH /v1/auth/me/password (returns 204), then bubbles success to the parent.
 *
 * Why a popup vs a route: rotation is a one-shot blocking step at login — embedding
 * it in the login flow avoids a second route + extra navigation state.
 */
export function ChangePasswordDialog({
  accessToken,
  onSuccess,
  onClose,
}: ChangePasswordDialogProps) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [clientError, setClientError] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: (body: ChangePasswordBody) =>
      apiClient.patch("/v1/auth/me/password", body, {
        headers: { Authorization: `Bearer ${accessToken}` },
      }),
    onSuccess,
  });

  const newValid = isPasswordValid(newPassword);
  const confirmMatch = newPassword === confirmPassword;
  const canSubmit = newValid && confirmMatch && currentPassword.length > 0;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setClientError(null);
    if (!confirmMatch) {
      setClientError("Mật khẩu xác nhận không khớp");
      return;
    }
    change.mutate({ current_password: currentPassword, new_password: newPassword });
  }

  const serverError = change.error ? translateApiError(change.error) : undefined;
  const errorMsg = clientError ?? serverError;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className="relative w-full max-w-sm rounded-lg border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Đổi mật khẩu</h2>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Đóng">
            <X size={16} />
          </Button>
        </div>

        <p className="mb-4 text-sm text-muted-foreground">
          Bạn cần đổi mật khẩu trước khi tiếp tục sử dụng hệ thống.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="current">Mật khẩu hiện tại</Label>
            <Input
              id="current"
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </div>

          <PasswordField
            id="new"
            label="Mật khẩu mới"
            value={newPassword}
            onChange={setNewPassword}
            autoComplete="new-password"
            required
            showRules
          />

          <PasswordField
            id="confirm"
            label="Xác nhận mật khẩu mới"
            value={confirmPassword}
            onChange={setConfirmPassword}
            autoComplete="new-password"
            required
            error={
              confirmPassword && !confirmMatch
                ? "Mật khẩu xác nhận không khớp"
                : undefined
            }
          />

          {errorMsg && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {errorMsg}
            </p>
          )}

          <Button type="submit" className="w-full" disabled={change.isPending || !canSubmit}>
            {change.isPending ? "Đang đổi…" : "Đổi mật khẩu"}
          </Button>
        </form>
      </div>
    </div>,
    document.body,
  );
}
